import { HttpClient, HttpEventType, HttpHeaders, HttpParams, HttpResponse } from '@angular/common/http';
import { Component, ElementRef, Inject, OnInit, ViewChild } from '@angular/core';
import { UntypedFormControl, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { API_BASE_URL_SERVICE } from '../../../../../projects/kiene-core/src/lib/kiene-core.config';
import { MessageService } from '../../../../../projects/kiene-core/src/lib/services/message-service/message.service';
import { KieneTableService } from '../../../components/kiene-table/kiene-table.service';
import { Institution } from '../../institutionen/institution';
import { SonstigerDebitor } from '../../sonstige-debitoren/sonstiger-debitor';
import { SonstigerErloes, SonstigerErloesPreis } from '../sonstiger-erloes';

@Component({
    selector: 'kiene-sonstiger-erloes-dialog',
    templateUrl: './sonstiger-erloes-dialog.component.html',
    styleUrls: ['./sonstiger-erloes-dialog.component.scss'],
})
export class SonstigerErloesDialogComponent implements OnInit {
    @ViewChild('fileSelector') fileSelector: ElementRef<HTMLInputElement>;

    isEdit = false;
    locked = false;
    partnerArt: 'INSTITUTION' | 'DEBITOR' = 'INSTITUTION';
    belegnrCtrl = new UntypedFormControl('');
    datumCtrl = new UntypedFormControl(new Date(), [Validators.required]);
    leistVonCtrl = new UntypedFormControl(null);
    leistBisCtrl = new UntypedFormControl(null);
    kommentarCtrl = new UntypedFormControl('');
    freigegebenCtrl = new UntypedFormControl(false);
    institution: Institution = null;
    debitor: SonstigerDebitor = null;
    kunde: any = null;
    preise: Array<SonstigerErloesPreis & { erloes?: any }> = [];
    mwstSaetze = [0, 7, 19];
    aktivParams = new HttpParams().set('aktiv', '1');
    erloesParams = new HttpParams().set('erloes_sonstig', '1').set('aktiv', '1');
    private id: number = null;
    status = 1;
    betragOffen = 0;
    existingPdfId: number = null;
    filesToUpload: File[] = [];
    filesProgressValues: number[] = [];
    filesUploadStatus: number[] = [];
    saving = false;

    constructor(
        public dialogRef: MatDialogRef<SonstigerErloesDialogComponent>,
        @Inject(MAT_DIALOG_DATA) private data: { erloes: SonstigerErloes },
        @Inject(API_BASE_URL_SERVICE) private apiBaseUrl: string,
        private api: KieneTableService,
        private http: HttpClient,
        private messageService: MessageService,
    ) {}

    ngOnInit(): void {
        this.addPreis();
        const e = this.data?.erloes;
        if (!e?.sonstiger_erloes_id) {
            return;
        }
        this.isEdit = true;
        this.id = e.sonstiger_erloes_id;
        this.locked = !!e.primanota_id;
        this.status = Number(e.status || 1);
        this.betragOffen = Number(e.betrag_offen || 0);
        this.existingPdfId = e.pdf_id ? Number(e.pdf_id) : null;
        this.belegnrCtrl.setValue(e.belegnr);
        this.datumCtrl.setValue(e.datum ? new Date(e.datum) : new Date());
        this.leistVonCtrl.setValue(e.leistungsdatum_von ? new Date(e.leistungsdatum_von) : null);
        this.leistBisCtrl.setValue(e.leistungsdatum_bis ? new Date(e.leistungsdatum_bis) : null);
        this.kommentarCtrl.setValue(e.kommentar);
        this.freigegebenCtrl.setValue(e.freigegeben > 0);
        if (e.sonstiger_debitor_id) {
            this.partnerArt = 'DEBITOR';
            this.debitor = {
                sonstiger_debitor_id: e.sonstiger_debitor_id,
                bezeichnung: e.partner,
            };
        } else if (e.institution_id) {
            this.partnerArt = 'INSTITUTION';
            this.institution = { institution_id: e.institution_id, bezeichnung: e.partner };
        }
        if (e.kunde_id) {
            this.kunde = { kunde_id: e.kunde_id, bezeichnung: e.kunde };
        }
        const parsed = this.parsePreise(e);
        if (parsed.length) {
            this.preise = parsed;
        }
        if (this.locked) {
            this.belegnrCtrl.disable();
            this.datumCtrl.disable();
            this.freigegebenCtrl.disable();
        }
    }

    addPreis() {
        this.preise.push({
            position: this.preise.length + 1,
            bezeichnung: '',
            kosten_netto: 0,
            mwstsatz: 0,
            fibu_erloes_konto_id: null,
            erloes: null,
        });
    }

    removePreis(i: number) {
        if (this.preise.length < 2) {
            return;
        }
        this.preise.splice(i, 1);
    }

    onPartnerArtChange() {
        if (this.partnerArt === 'INSTITUTION') {
            this.debitor = null;
        } else {
            this.institution = null;
        }
    }

    isValid(): boolean {
        const hasPartner =
            this.partnerArt === 'INSTITUTION' ? !!this.institution?.institution_id : !!this.debitor?.sonstiger_debitor_id;
        const hasPreis = this.preise.some(p => Number(p.kosten_netto) > 0 && !!p.erloes?.fibu_konto_id);
        return this.datumCtrl.valid && hasPartner && hasPreis;
    }

    openFileSelector() {
        this.fileSelector?.nativeElement?.click();
    }

    handleFiles(event: Event) {
        const input = event.target as HTMLInputElement;
        if (!input?.files?.length) {
            return;
        }
        this.filesToUpload = [input.files.item(0)];
        this.filesProgressValues = [0];
        this.filesUploadStatus = [0];
        input.value = '';
    }

    removeFile() {
        this.filesToUpload = [];
        this.filesProgressValues = [];
        this.filesUploadStatus = [];
    }

    save() {
        if (!this.isValid() || this.locked || this.saving) {
            return;
        }
        this.saving = true;
        const fd = new FormData();
        if (this.id) {
            fd.append('sonstiger_erloes_id', String(this.id));
        }
        fd.append('belegnr', (this.belegnrCtrl.value || '').trim());
        fd.append('datum', this.toDateStr(this.datumCtrl.value) || '');
        const lv = this.toDateStr(this.leistVonCtrl.value);
        const lb = this.toDateStr(this.leistBisCtrl.value);
        if (lv) {
            fd.append('leistungsdatum_von', lv);
        }
        if (lb) {
            fd.append('leistungsdatum_bis', lb);
        }
        fd.append('kommentar', (this.kommentarCtrl.value || '').trim());
        fd.append('freigegeben', this.freigegebenCtrl.value ? '1' : '0');
        if (this.partnerArt === 'INSTITUTION' && this.institution?.institution_id) {
            fd.append('institution_id', String(this.institution.institution_id));
        }
        if (this.partnerArt === 'DEBITOR' && this.debitor?.sonstiger_debitor_id) {
            fd.append('sonstiger_debitor_id', String(this.debitor.sonstiger_debitor_id));
        }
        if (this.kunde?.kunde_id) {
            fd.append('kunde_id', String(this.kunde.kunde_id));
        }
        const preise = this.preise
            .filter(p => Number(p.kosten_netto) > 0 && p.erloes?.fibu_konto_id)
            .map((p, i) => ({
                position: i + 1,
                bezeichnung: p.bezeichnung || null,
                kosten_netto: Number(p.kosten_netto),
                mwstsatz: Number(p.mwstsatz || 0),
                fibu_erloes_konto_id: p.erloes.fibu_konto_id,
            }));
        fd.append('preise', JSON.stringify(preise));
        if (this.filesToUpload.length > 0) {
            fd.append('dokument', this.filesToUpload[0]);
        }

        const options = {
            headers: new HttpHeaders('Phito-Type: fileupload'),
            reportProgress: true as const,
            observe: 'events' as const,
        };
        this.http.post<any>(this.apiBaseUrl + 'sonstiger_erloes/update.php', fd, options).subscribe({
            next: event => {
                if (event.type === HttpEventType.UploadProgress && event.total) {
                    this.filesProgressValues[0] = Math.round((100 * event.loaded) / event.total);
                } else if (event instanceof HttpResponse) {
                    this.filesUploadStatus[0] = 1;
                }
            },
            error: err => {
                this.saving = false;
                this.filesUploadStatus[0] = 2;
                this.messageService.errorMessage('Speichern fehlgeschlagen.', err);
            },
            complete: () => {
                this.saving = false;
                this.messageService.infoMessage('Sonstiger Erlös gespeichert.');
                this.dialogRef.close(true);
            },
        });
    }

    ausgleichen() {
        if (!this.id) {
            return;
        }
        this.api
            .post(this.apiBaseUrl + 'sonstiger_erloes/ausgleichen.php', {
                sonstiger_erloes_id: this.id,
                bezahlt_am: this.toDateStr(new Date()),
                bezahlt_betrag: this.betragOffen,
            })
            .subscribe({
                next: () => {
                    this.messageService.infoMessage('Beleg ausgeglichen.');
                    this.dialogRef.close(true);
                },
                error: err => this.messageService.errorMessage('Ausgleichen fehlgeschlagen.', err),
            });
    }

    cancel() {
        this.dialogRef.close(false);
    }

    private parsePreise(e: SonstigerErloes): Array<SonstigerErloesPreis & { erloes?: any }> {
        let raw: any = e.preise || e.preise_as_json;
        if (typeof raw === 'string') {
            try {
                raw = JSON.parse(raw);
            } catch {
                raw = [];
            }
        }
        if (!Array.isArray(raw)) {
            return [];
        }
        return raw.map((p: SonstigerErloesPreis) => ({
            ...p,
            erloes: p.fibu_erloes_konto_id
                ? { fibu_konto_id: p.fibu_erloes_konto_id, konto_nr: p.erloes_konto_nr, bezeichnung: p.erloes_konto }
                : null,
        }));
    }

    private toDateStr(d: Date | string): string | null {
        if (!d) {
            return null;
        }
        const dt = d instanceof Date ? d : new Date(d);
        if (isNaN(dt.getTime())) {
            return null;
        }
        const m = String(dt.getMonth() + 1).padStart(2, '0');
        const day = String(dt.getDate()).padStart(2, '0');
        return `${dt.getFullYear()}-${m}-${day}`;
    }
}
